/**
 * SEOHead Component
 * 
 * A reusable component for rendering SEO meta tags, Open Graph, Twitter Cards,
 * and JSON-LD schema markup. Integrates with the useSeo hook for i18n support.
 * 
 * Features:
 * - Dynamic meta tag generation
 * - Open Graph and Twitter Card support
 * - JSON-LD schema markup
 * - i18n support
 * - Platform-specific optimization
 * 
 * =====================================================================
 * NOTE ON SEO IMPLEMENTATION STRATEGY
 * =====================================================================
 * The application uses two different approaches for SEO meta tags:
 * 
 * 1. SEOHead Component (this file):
 *    - Used primarily for standard pages (home, about, legal, etc.)
 *    - Provides a centralized, consistent approach to SEO
 *    - Gets data from useSeo hook which loads from i18n files
 *    - Handles all standard meta tags including hreflang for all supported languages
 * 
 * 2. Direct Head Implementation (in DownloaderPage.tsx):
 *    - Used for platform-specific pages that need custom SEO logic
 *    - Handles complex fallback scenarios across multiple translation namespaces
 *    - Generates dynamic FAQPage schema based on platform-specific content
 *    - Still uses usePlatformSeo hook for hreflang tags
 * 
 * This dual approach allows for both consistency and flexibility depending on
 * the page requirements. When maintaining the codebase, be aware of which
 * approach is being used for the page you're working on.
 */

import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useSeo, SeoConfig } from '@/lib/hooks/useSeo';

interface SEOHeadProps {
  pageKey?: string;
  customConfig?: Partial<SeoConfig>;
  children?: React.ReactNode;
}

const SEOHead: React.FC<SEOHeadProps> = ({ 
  pageKey, 
  customConfig,
  children 
}) => {
  const router = useRouter();
  const { locale = 'en', asPath } = router;
  const { metaTags } = useSeo(pageKey);
  
  // Create proper URL for current page with correct locale
  const baseUrl = 'https://downloadmod.com';
  const currentFullUrl = locale === 'en' 
    ? `${baseUrl}${asPath}` 
    : `${baseUrl}/${locale}${asPath === '/' ? '' : asPath}`;
  
  // Override canonical, og:url, and og:locale to ensure they point to the current page's URL and locale
  const localeMap: Record<string, string> = {
    en: 'en_US',
    vi: 'vi_VN',
    it: 'it_IT',
    fr: 'fr_FR',
    es: 'es_ES',
    de: 'de_DE',
    pt: 'pt_PT',
    ja: 'ja_JP',
    ko: 'ko_KR',
    zh: 'zh_CN',
    ar: 'ar_AR',
    ru: 'ru_RU',
    th: 'th_TH',
    id: 'id_ID',
    tr: 'tr_TR',
    nl: 'nl_NL',
    pl: 'pl_PL',
    // Thêm các ngôn ngữ khác nếu cần
  };

  const ogLocale = localeMap[locale] || 'en_US';

  const updatedMetaTags = {
    ...metaTags,
    canonical: currentFullUrl,
    openGraph: {
      ...metaTags.openGraph,
      url: currentFullUrl,
      locale: ogLocale
    },
    // Sử dụng giá trị robots từ customConfig nếu có, nếu không thì mặc định là 'index, follow'
    robots: customConfig?.robots || 'index, follow'
  };
  
  const finalMetaTags = customConfig 
    ? { ...updatedMetaTags, ...customConfig }
    : updatedMetaTags;

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{finalMetaTags.title}</title>
      <meta name="description" content={finalMetaTags.description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="icon" href="/favicon.ico" />
      
      {/* Robots Meta Tag */}
      <meta name="robots" content={finalMetaTags.robots} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={finalMetaTags.openGraph?.type || 'website'} />
      <meta property="og:url" content={finalMetaTags.openGraph?.url || ''} />
      <meta property="og:title" content={finalMetaTags.openGraph?.title || finalMetaTags.title} />
      <meta property="og:description" content={finalMetaTags.openGraph?.description || finalMetaTags.description} />
      <meta property="og:image" content={finalMetaTags.openGraph?.image || '/downloadmod_logo.png'} />
      <meta property="og:site_name" content="DownloadMod" />
      {finalMetaTags.openGraph?.locale && (
        <meta property="og:locale" content={finalMetaTags.openGraph.locale} />
      )}
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={finalMetaTags.twitter?.title || finalMetaTags.openGraph?.title || finalMetaTags.title} />
      <meta property="twitter:description" content={finalMetaTags.twitter?.description || finalMetaTags.openGraph?.description || finalMetaTags.description} />
      <meta property="twitter:image" content={finalMetaTags.twitter?.image || finalMetaTags.openGraph?.image || '/downloadmod_logo.png'} />
      
      {/* Canonical Link - Always points to the current page's URL */}
      <link rel="canonical" href={finalMetaTags.canonical} />
      
      {/* Hreflang Tags - Include all language variants */}
      {finalMetaTags.hreflangs?.map((hreflang) => {
        // Thêm thẻ hreflang cho từng ngôn ngữ
        return (
          <link 
            key={hreflang.hreflang}
            rel="alternate" 
            hrefLang={hreflang.hreflang} 
            href={hreflang.href} 
          />
        );
      })}
      
      {/* JSON-LD Schema */}
      {finalMetaTags.jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(finalMetaTags.jsonLd)
          }}
        />
      )}

      {/* Additional meta tags */}
      {children}
    </Head>
  );
};

export default SEOHead;
